import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { LogOut, Leaf, Trash2, Phone, Mail, MapPin, Loader2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "closed"];
const badge = {
  new: "bg-clay/15 text-clay-dark border-clay/30",
  contacted: "bg-forest/10 text-forest border-forest/30",
  closed: "bg-moss/10 text-moss border-moss/30",
};

const AdminDashboard = () => {
  const { token, admin, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, auth);
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, auth);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  const counts = {
    all: quotes.length,
    new: quotes.filter((q) => q.status === "new").length,
    contacted: quotes.filter((q) => q.status === "contacted").length,
    closed: quotes.filter((q) => q.status === "closed").length,
  };

  const fmt = (d) => new Date(d).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="bg-forest-deep text-bone">
        <div className="container-fl h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Leaf className="text-clay" size={24} strokeWidth={1.5} />
            <div className="leading-none">
              <p className="font-serif text-xl">Fresh Landscape</p>
              <p className="text-xs text-bone/50 tracking-wide">Quote Requests</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden sm:block text-sm text-bone/70">{admin?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm hover:text-clay transition-colors">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-fl py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[["all", "Total"], ["new", "New"], ["contacted", "Contacted"], ["closed", "Closed"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`text-left p-6 border transition-colors ${filter === k ? "bg-forest text-bone border-forest" : "bg-white border-line hover:border-forest"}`}
            >
              <p className={`text-4xl font-serif ${filter === k ? "text-bone" : "text-ink"}`}>{counts[k]}</p>
              <p className={`text-sm mt-1 ${filter === k ? "text-bone/70" : "text-moss"}`}>{label}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-forest" size={32} /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20 text-moss">
            <Inbox className="mx-auto mb-4 text-line" size={48} />
            <p>No quote requests {filter !== "all" ? `marked "${filter}"` : "yet"}.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="quotes-list">
            {shown.map((q) => (
              <div key={q.id} data-testid={`quote-row-${q.id}`} className="bg-white border border-line p-6 md:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl text-ink">{q.name}</h3>
                      <span className={`text-xs uppercase tracking-wide px-3 py-1 border ${badge[q.status]}`}>{q.status}</span>
                      {q.service && <span className="text-xs uppercase tracking-wide px-3 py-1 border border-line text-moss">{q.service}</span>}
                    </div>
                    <p className="text-sm text-moss mt-1">{fmt(q.created_at)}</p>
                  </div>
                  <button onClick={() => remove(q.id)} data-testid={`delete-${q.id}`} className="text-moss hover:text-red-600 transition-colors" aria-label="Delete">
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm">
                  <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-ink hover:text-clay break-all"><Mail size={15} /> {q.email}</a>
                  {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-ink hover:text-clay"><Phone size={15} /> {q.phone}</a>}
                  {q.address && <span className="flex items-center gap-2 text-moss"><MapPin size={15} /> {q.address}</span>}
                </div>

                {q.message && <p className="mt-4 text-moss bg-sand p-4 border-l-2 border-clay">{q.message}</p>}

                <div className="flex flex-wrap gap-2 mt-5">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(q.id, s)}
                      data-testid={`set-${s}-${q.id}`}
                      disabled={q.status === s}
                      className={`text-xs uppercase tracking-wide px-4 py-2 border transition-colors ${q.status === s ? "bg-forest text-bone border-forest cursor-default" : "border-line text-moss hover:border-forest hover:text-forest"}`}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
